import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import MessagePage from '@dashboard/components/pages/MessagePage';
import { Container } from '@mui/material';

import LoadingIndicator from '../components/loading/LoadingIndicator';

type TState = { status: 'LOADING' | 'ERROR' } | { status: 'COMPLETED'; href: string };

export default function Auth0Callback(): JsxNode {
  const { handleRedirectCallback } = useAuth0();
  const [state, setState] = useState<TState>({ status: 'LOADING' });

  const [searchParams] = useSearchParams();

  useEffect(() => {
    void handleRedirectCallback()
      .then((result) => {
        setState({
          status: 'COMPLETED',
          href: result.appState?.returnTo ?? '/',
        });
      })
      .catch(() => {
        setState({ status: 'ERROR' });
      });
  }, [state, handleRedirectCallback, setState]);

  switch (state.status) {
    case 'LOADING':
      return (
        <Container maxWidth="sm">
          <LoadingIndicator />
        </Container>
      );
    case 'ERROR':
      return (
        <MessagePage
          icon="ERROR"
          title={getErrorTitle(searchParams)}
          description={getErrorDescription(searchParams)}
          action={getErrorButton(searchParams)}
        />
      );
    case 'COMPLETED':
      return <Navigate to={state.href} replace />;
  }
}

function isVerificationError(searchParams: URLSearchParams) {
  const description = searchParams.get('error_description');
  return description === 'Please verify your email before logging in.';
}

function getErrorButton(searchParams: URLSearchParams) {
  if (isVerificationError(searchParams)) {
    return null;
  }
  return (
    <ButtonLink variant="contained" href="/">
      Retry
    </ButtonLink>
  );
}

function getErrorTitle(searchParams: URLSearchParams) {
  if (isVerificationError(searchParams)) {
    return 'Verify your email address';
  }
  return 'Log-in error';
}

function getErrorDescription(searchParams: URLSearchParams) {
  if (isVerificationError(searchParams)) {
    return 'To get started using Waypoint, you’ll first need to verify your email address.';
  }
  return 'There was an error logging you in';
}
